import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import DisclaimerDialog from "@/components/legal/DisclaimerDialog";

/**
 * Mounts inside the authenticated shell. Decides whether the
 * DisclaimerDialog should open by reading:
 *
 *   1. `pendingDisclaimerCheck` from AuthContext — true only right after
 *      a fresh login (see `login()` in lib/auth.jsx). This enforces the
 *      "grace period for existing sessions" rule from the product spec:
 *      a user whose token is still valid from before the feature shipped
 *      will NOT be interrupted mid-session; they'll be prompted on their
 *      next login.
 *
 *   2. The user's `disclaimer_accepted_version` vs the value the backend
 *      reports as "current". For v1 we only need a null check; future
 *      versions bump in lockstep with `app_settings.disclaimer_version`.
 *
 * On accept we call `refresh()` to pull the updated user doc (now
 * carrying `disclaimer_accepted_version`) and clear the pending flag so
 * we don't re-open on the next render.
 */
const DisclaimerGate = () => {
  const { user, pendingDisclaimerCheck, clearDisclaimerCheck, refresh } = useAuth();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Only consider opening after a fresh login.
    if (!pendingDisclaimerCheck || !user) return;

    // Quick client-side short-circuit: if the user already has *any*
    // accepted version recorded, assume they're good until the backend
    // says otherwise on their next login. (The backend also re-validates
    // on accept, so a stale version bump can't slip through.)
    if (user.disclaimer_accepted_version) {
      clearDisclaimerCheck();
      return;
    }

    // Otherwise show the dialog. The dialog itself fetches the live
    // version + checkbox copy on open so we avoid baking copy into JS.
    setOpen(true);
  }, [user, pendingDisclaimerCheck, clearDisclaimerCheck]);

  const handleAccepted = async () => {
    setOpen(false);
    clearDisclaimerCheck();
    try {
      await refresh();
    } catch {
      /* non-blocking — user state is still correct via server on next hit */
    }
  };

  const handleError = () => {
    // If the status endpoint fails we don't want to wedge the user out
    // of the whole app. Close the dialog silently; they'll be prompted
    // again on next login.
    setOpen(false);
    clearDisclaimerCheck();
  };

  if (!open) return null;
  return <DisclaimerDialog open={open} onAccepted={handleAccepted} onError={handleError} />;
};

export default DisclaimerGate;
